/**
 * Market Radar client hooks.
 *
 * Read-only. These call the public radar endpoints and nothing else — there is
 * no trading, balance, position, or private exchange surface anywhere in this
 * feature.
 *
 * Rows are preserved across refreshes (`placeholderData` keeps the previous
 * response while the next is in flight), so a routine poll does not blank the
 * table or make it jump.
 */

import { useQuery } from "@tanstack/react-query";

export type RadarStatus = "live" | "warming" | "stale" | "unavailable";
export type RadarSource = "worker" | "published" | "store" | "none";
export type AttentionTier = "scan" | "watch" | "deep";
export type LifecycleState =
  | "DORMANT" | "EMERGING" | "ACCELERATING" | "ACTIVE" | "PEAK" | "DECAYING";

export interface RadarMetrics {
  volumeAcceleration: number | null;
  tradeRate: number | null;
  tradeRateAcceleration: number | null;
  priceVelocity: number | null;
  microVolatility: number | null;
  rangeExpansionRatio: number | null;
  relativeTurnoverShare: number | null;
  spreadBps: number | null;
  bookImbalance: number | null;
  depthQuality: number | null;
  aggressorRatio: number | null;
  liquidityStability: number | null;
  dataQuality: AttentionTier;
}

export interface RadarExecutability {
  score: number | null;
  executable: boolean | null;
  reasons: string[];
}

export interface RadarSymbolRow {
  symbol: string;
  baseCoin: string;
  tier: AttentionTier;
  dataQuality: AttentionTier;
  rank: number | null;
  previousRank: number | null;
  lifecycleState: LifecycleState;
  activityScore: number | null;
  rankingScore: number | null;
  smoothedActivityScore: number | null;
  scoreComponents: Record<string, number | null>;
  metrics: RadarMetrics;
  executability: RadarExecutability;
  /** Last traded price. Display context only, never a score input. */
  lastPrice: number | null;
  /** Exchange-reported rolling 24h turnover. Context only, never a score input. */
  turnover24hQuote: number | null;
  firstDetectedAt: string;
  stateChangedAt: string;
  updatedAt: string;
}

export interface RadarEnvelope {
  status: RadarStatus;
  source: RadarSource;
  snapshotAgeMs: number | null;
  generatedAt: string | null;
  warmupCompleteAt: string | null;
  universeSize: number;
  eligibleSize: number;
  tiers: { scan: number; watch: number; deep: number };
}

export interface RadarBoardResponse extends RadarEnvelope {
  count: number;
  symbols: RadarSymbolRow[];
}

export interface RadarMoversResponse extends RadarEnvelope {
  rising: Array<RadarSymbolRow & { rankMomentum: number }>;
  falling: Array<RadarSymbolRow & { rankMomentum: number }>;
}

export interface RadarLifecycleTransition {
  symbol: string;
  fromState: LifecycleState | null;
  toState: LifecycleState;
  atMs: number;
  activityScore: number | null;
  rankingScore: number | null;
  rank: number | null;
  tier: AttentionTier;
}

export interface RadarTierTransition {
  symbol: string;
  fromTier: AttentionTier;
  toTier: AttentionTier;
  atMs: number;
  rank: number | null;
  rankingScore: number | null;
}

export interface RadarEligibilityTransition {
  symbol: string;
  fromEligible: boolean | null;
  toEligible: boolean;
  reasons: string[];
  lastRank: number | null;
  lastActivityScore: number | null;
  lastRankingScore: number | null;
  lastLifecycleState: LifecycleState | null;
  lastTier: AttentionTier;
  turnover24hQuote: number | null;
  atMs: number;
}

export interface RadarSymbolResponse extends RadarEnvelope {
  symbol: RadarSymbolRow;
  lifecycleTransitions: RadarLifecycleTransition[];
  tierTransitions: RadarTierTransition[];
  eligibilityTransitions: RadarEligibilityTransition[];
}

export interface RadarHistoryRow {
  symbol: string;
  bucketStartMs: number;
  resolution: "1m" | "5m";
  rank: number | null;
  activityScore: number | null;
  rankingScore: number | null;
  lifecycleState: LifecycleState;
  tier: AttentionTier;
  dataQuality: AttentionTier;
  turnover24hQuote: number | null;
  volumeAcceleration: number | null;
  spreadBps: number | null;
  [key: string]: unknown;
}

export interface RadarHistoryResponse {
  symbol: string;
  bucket: "1m" | "5m";
  count: number;
  provenance?: { nullMeaning?: string; dataQualityMeaning?: Record<string, string> };
  rows: RadarHistoryRow[];
}

const REFRESH_MS = 5_000;

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    // A 404 for an unranked symbol is a normal outcome, not a failure.
    throw new Error(`${response.status}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export function useMarketRadarBoard() {
  return useQuery<RadarBoardResponse>({
    queryKey: ["/api/market-radar", "board"],
    queryFn: () => fetchJson<RadarBoardResponse>("/api/market-radar?limit=500"),
    refetchInterval: REFRESH_MS,
    // Keep the previous board on screen while the next one loads, so a routine
    // refresh never blanks or reorders the table under the cursor.
    placeholderData: (previous) => previous,
    staleTime: 0,
  });
}

export function useMarketRadarMovers() {
  return useQuery<RadarMoversResponse>({
    queryKey: ["/api/market-radar/movers"],
    queryFn: () => fetchJson<RadarMoversResponse>("/api/market-radar/movers?limit=25"),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });
}

export function useMarketRadarSymbol(symbol: string | null) {
  return useQuery<RadarSymbolResponse>({
    queryKey: ["/api/market-radar/symbol", symbol],
    queryFn: () => fetchJson<RadarSymbolResponse>(`/api/market-radar/${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

export function useMarketRadarHistory(symbol: string | null, bucket: "1m" | "5m" = "1m") {
  return useQuery<RadarHistoryResponse>({
    queryKey: ["/api/market-radar/history", symbol, bucket],
    queryFn: () => fetchJson<RadarHistoryResponse>(
      `/api/market-radar/${encodeURIComponent(symbol!)}/history?bucket=${bucket}&limit=180`,
    ),
    enabled: Boolean(symbol),
    // History moves slowly; polling it as fast as the board would be wasteful.
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
    retry: false,
  });
}
